import { createEffect, For, onCleanup, Show } from "solid-js"
import { useDialog } from "@opencode-ai/ui/context/dialog"
import { ButtonV2 } from "@opencode-ai/ui/v2/button-v2"
import { Dialog, DialogBody, DialogFooter, DialogHeader, DialogTitle } from "@opencode-ai/ui/v2/dialog-v2"
import { useLanguage } from "@/context/language"
import { fileSyncActive, fileSyncErrorKey, fileSyncFailed, type FileIntegrationScope, type useFileIntegrations } from "@/datox/file-integrations"
import type { DatoxFileSync } from "@/datox/api"

export function DatoxFilesIntegrationsAction(props: {
  scope?: FileIntegrationScope
  disabled: boolean
  controller: ReturnType<typeof useFileIntegrations>
}) {
  const language = useLanguage()
  const dialog = useDialog()
  let mounted = true
  let visible = false
  onCleanup(() => { mounted = false; if (visible) dialog.close() })
  const open = () => {
    const scope = props.scope
    if (!scope || props.disabled || !mounted) return
    void props.controller.load()
    dialog.show(() => {
      visible = true
      onCleanup(() => { visible = false })
      const current = () => mounted && props.scope?.projectID === scope.projectID && props.scope?.periodID === scope.periodID
      createEffect(() => { if (!current()) dialog.close() })
      return <Dialog fit>
        <DialogHeader closeLabel={language.t("common.close")}><DialogTitle>{language.t("datox.fileIntegrations.title")}</DialogTitle></DialogHeader>
        <DialogBody class="flex w-[460px] max-w-full flex-col gap-4 p-4">
          <p class="text-14-regular text-v2-text-text-muted">{language.t("datox.fileIntegrations.description", { period: scope.periodName })}</p>
          <Show when={props.controller.state.loading}><p role="status">{language.t("datox.fileIntegrations.loading")}</p></Show>
          <Show when={props.controller.state.loadError}><div role="alert"><p>{language.t("datox.fileIntegrations.loadFailed")}</p><ButtonV2 onClick={() => void props.controller.load()}>{language.t("datox.fileIntegrations.retry")}</ButtonV2></div></Show>
          <Show when={!props.controller.state.loading && !props.controller.state.loadError && !props.controller.state.connections.length}><p>{language.t("datox.fileIntegrations.empty")}</p></Show>
          <For each={props.controller.state.connections}>{(connection) => {
            const job = () => props.controller.state.jobs[connection.id]
            const busy = () => props.controller.state.busyID === connection.id || fileSyncActive(job())
            return <section class="flex flex-col gap-3 rounded-lg border border-v2-border-border-base p-4">
              <div class="flex items-center justify-between gap-4"><h3 class="text-14-medium">{language.t("datox.fileIntegrations.alphaDesk")}</h3><span class="text-12-regular text-v2-text-text-muted">{language.t("datox.fileIntegrations.connected")}</span></div>
              <p class="text-13-regular text-v2-text-text-muted">{language.t("datox.fileIntegrations.sourceDescription")}</p>
              <Show when={job()}>{(value) => <FileSyncStatus job={value()} period={scope.periodName} />}</Show>
              <Show when={props.controller.state.busyID === connection.id}><p role="status">{language.t("datox.fileIntegrations.checking")}</p></Show>
              <ButtonV2 variant="contrast" aria-busy={busy()} disabled={!current() || props.disabled || props.controller.state.loading || props.controller.state.loadError || !!props.controller.state.busyID || fileSyncActive(job())}
                onClick={() => void props.controller.sync(connection.id)}>{language.t(fileSyncFailed(job()) ? "datox.fileIntegrations.retryFiles" : "datox.fileIntegrations.sync")}</ButtonV2>
            </section>
          }}</For>
          <Show when={props.controller.state.requestError}><p role="alert" class="workflow-error">{language.t(fileSyncErrorKey(props.controller.state.requestError))}</p></Show>
        </DialogBody>
        <DialogFooter><ButtonV2 variant="neutral" onClick={() => dialog.close()}>{language.t("common.close")}</ButtonV2></DialogFooter>
      </Dialog>
    })
  }
  return <button type="button" class="workflow-action" disabled={props.disabled || !props.scope} onClick={open}>{language.t("datox.fileIntegrations.title")}</button>
}

function FileSyncStatus(props: { job: DatoxFileSync; period: string }) {
  const language = useLanguage()
  const summary = () => {
    const job = props.job
    if (job.status === "queued" || job.status === "discovering") return language.t("datox.fileIntegrations.checking")
    if (job.status === "syncing") return language.t("datox.fileIntegrations.progress", { processed: job.imported + job.skipped + job.failed, matched: job.matched })
    if (fileSyncFailed(job)) return job.error ? language.t(fileSyncErrorKey(job.error)) : language.t("datox.fileIntegrations.partial", { imported: job.imported, failed: job.failed })
    if (!job.matched) return language.t("datox.fileIntegrations.noFiles", { period: props.period })
    if (!job.imported) return language.t("datox.fileIntegrations.alreadyPresent")
    return language.t("datox.fileIntegrations.completed", { imported: job.imported, period: props.period })
  }
  return <div class="space-y-2 text-13-regular" role="status" aria-live="polite">
    <p>{summary()}</p>
    <Show when={props.job.skipped > 0 && props.job.imported > 0}><p>{language.t("datox.fileIntegrations.skipped", { skipped: props.job.skipped })}</p></Show>
    <ul class="max-h-48 overflow-auto"><For each={props.job.items.filter((item) => item.status === "failed")}>{(item) => <li class="py-1"><strong class="break-all">{item.filename}</strong><p>{language.t(fileSyncErrorKey(item.error))}</p></li>}</For></ul>
  </div>
}
